import { forwardRef } from 'react';

import { cva } from 'cva';
import {
  GridListItem,
  type GridListItemProps,
} from 'react-aria-components/GridList';

import { Text, type TextProps } from '../text/text';

import styles from './list_view.module.css';

const listItemVariants = cva({
  base: styles.list_item,
});

export type ListItemProps = GridListItemProps;
export type ListItemTextProps = Omit<TextProps, 'variant' | 'color'>;
export type ListItemDescriptionProps = Omit<TextProps, 'variant' | 'color'>;

/**
 * ListItem - A list row item that can be used in a `ListView`.
 *
 * See https://react-spectrum.adobe.com/react-aria/GridList.html for more details.
 *
 * ```tsx
 * <ListItem id={user.id} textValue={user.name}>
 *   <ListItemText>{user.name}</ListItemText>
 *   <ListItemDescription>{user.role}</ListItemDescription>
 * </ListItem>
 * ```
 */
export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
  ({ children, className, ...props }, ref) => {
    const classes = listItemVariants({ className });
    const textValue = typeof children === 'string' ? children : undefined;

    return (
      <GridListItem
        ref={ref}
        textValue={textValue}
        className={classes}
        {...props}
      >
        {children}
      </GridListItem>
    );
  },
);

/**
 * ListItemText - Primary text for a `ListItem`.
 */
export const ListItemText = forwardRef<HTMLElement, ListItemTextProps>(
  (props, ref) => {
    return <Text ref={ref} variant="value" color="high_emphasis" {...props} />;
  },
);

/**
 * ListItemDescription - Secondary description text for a `ListItem`.
 */
export const ListItemDescription = forwardRef<
  HTMLElement,
  ListItemDescriptionProps
>((props, ref) => {
  return (
    <Text
      ref={ref}
      variant="body"
      color="medium_emphasis"
      elementType="p"
      {...props}
    />
  );
});
